/**
 * CommentThread - Thread view for a comment and its replies
 *
 * Features:
 * - Original comment display
 * - Threaded replies
 * - Reply input
 * - Resolve button
 * - Martha integration badge
 */

import { useState } from 'react';
import { Check, Send, Sparkles, User, CornerDownRight } from 'lucide-react';
import type { InlineComment, CommentReply } from './InlineComment';

interface CommentThreadProps {
  comment: InlineComment;
  replies: CommentReply[];
  onReply: (content: string) => void;
  onResolve: () => void;
  onAskMartha?: (context: { selectedText: string; commentContent: string }) => void;
  isSubmitting?: boolean;
}

export function CommentThread({
  comment,
  replies,
  onReply,
  onResolve,
  onAskMartha,
  isSubmitting,
}: CommentThreadProps) {
  const [replyContent, setReplyContent] = useState('');

  const handleSubmitReply = () => {
    if (!replyContent.trim()) return;
    onReply(replyContent);
    setReplyContent('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmitReply();
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const isResolved = comment.status === 'resolved';

  return (
    <div className="flex flex-col">
      {/* Quoted text */}
      <div className="px-3 py-2 bg-neutral-50 border-l-2 border-coral-300 text-sm text-neutral-600 italic mb-3 rounded-r">
        "{comment.selectedText}"
      </div>

      {/* Original comment */}
      <CommentBubble
        authorName={comment.authorName}
        authorType={comment.authorType}
        content={comment.content}
        createdAt={comment.createdAt}
      />

      {/* Replies */}
      {replies.length > 0 && (
        <div className="ml-4 mt-2 space-y-2 border-l border-neutral-200 pl-3">
          {replies.map((reply) => (
            <CommentBubble
              key={reply.id}
              authorName={reply.authorName}
              authorType={reply.authorType}
              content={reply.content}
              createdAt={reply.createdAt}
              isReply
            />
          ))}
        </div>
      )}

      {/* Actions and reply input */}
      {!isResolved && (
        <div className="mt-3 space-y-2">
          {/* Reply input */}
          <div className="flex gap-2">
            <textarea
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Reply or type @martha for AI help..."
              rows={2}
              className="flex-1 px-3 py-2 text-sm border border-neutral-200 rounded-lg resize-none
                       focus:outline-none focus:ring-1 focus:ring-coral-200 focus:border-coral-300"
            />
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={handleSubmitReply}
                disabled={!replyContent.trim() || isSubmitting}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium
                         bg-coral-500 text-white rounded-lg hover:bg-coral-600
                         disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Send size={14} />
                Reply
              </button>

              {onAskMartha && (
                <button
                  onClick={() => onAskMartha({
                    selectedText: comment.selectedText,
                    commentContent: comment.content,
                  })}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium
                           text-coral-600 hover:bg-coral-50 rounded-lg transition-colors"
                >
                  <Sparkles size={14} />
                  @martha
                </button>
              )}
            </div>

            <button
              onClick={onResolve}
              className="flex items-center gap-1 px-3 py-1.5 text-sm
                       text-neutral-600 hover:text-emerald-600 hover:bg-emerald-50
                       rounded-lg transition-colors"
            >
              <Check size={14} />
              Resolve
            </button>
          </div>

          <p className="text-xs text-neutral-400">
            Press Cmd+Enter to reply
          </p>
        </div>
      )}

      {/* Resolved state */}
      {isResolved && (
        <div className="mt-3 flex items-center gap-2 text-sm text-emerald-600">
          <Check size={16} />
          <span>This comment has been resolved</span>
        </div>
      )}
    </div>
  );
}

interface CommentBubbleProps {
  authorName: string;
  authorType: 'user' | 'martha';
  content: string;
  createdAt: string;
  isReply?: boolean;
}

function CommentBubble({
  authorName,
  authorType,
  content,
  createdAt,
  isReply,
}: CommentBubbleProps) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className={`flex gap-2 ${isReply ? 'text-sm' : ''}`}>
      {/* Avatar */}
      <div
        className={`flex-shrink-0 rounded-full flex items-center justify-center
                   ${authorType === 'martha'
                     ? 'bg-coral-100 text-coral-600'
                     : 'bg-neutral-100 text-neutral-600'
                   }
                   ${isReply ? 'w-6 h-6' : 'w-8 h-8'}`}
      >
        {authorType === 'martha' ? (
          <Sparkles size={isReply ? 12 : 14} />
        ) : (
          <User size={isReply ? 12 : 14} />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className={`font-medium text-neutral-800 ${isReply ? 'text-xs' : 'text-sm'}`}>
            {authorName}
            {authorType === 'martha' && (
              <span className="ml-1 text-xs text-coral-500">(AI)</span>
            )}
          </span>
          <span className={`text-neutral-400 ${isReply ? 'text-[10px]' : 'text-xs'}`}>
            {formatDate(createdAt)}
          </span>
        </div>
        <p className={`text-neutral-600 whitespace-pre-wrap ${isReply ? 'text-xs' : 'text-sm'}`}>
          {content}
        </p>
      </div>
    </div>
  );
}

export default CommentThread;
